import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from './reporter';

interface Props {
  /** Rendered instead of the children after an error. */
  fallback: ReactNode | ((error: unknown, reset: () => void) => ReactNode);
  /** Changing this clears the error (e.g. the route path, so navigating away recovers). */
  resetKey?: unknown;
  children: ReactNode;
}

interface State {
  error: unknown;
  resetKey: unknown;
}

/** Sentry's ErrorBoundary in shape: catch, report, render a fallback. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error ?? new Error('Unknown error') };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) return { error: null, resetKey: props.resetKey };
    return null;
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    captureException(error, { componentStack: info.componentStack });
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      return typeof fallback === 'function' ? fallback(error, this.reset) : fallback;
    }
    return this.props.children;
  }
}
